import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/shop';
import { formatMoney } from '../brand/brand';
import { usePageTitle } from '../a11y/usePageTitle';
import type { CounterPayment, MyVoucher } from '../api/types';
import { refreshPasses } from '../lib/offlinePasses';

export function MyVouchers() {
  const [vouchers, setVouchers] = useState<MyVoucher[] | null>(null);
  const [owed, setOwed] = useState<CounterPayment[]>([]);

  useEffect(() => {
    api.openCounterPayments().then(setOwed).catch(() => setOwed([]));
  }, []);

  usePageTitle('My vouchers');

  useEffect(() => {
    let cancelled = false;

    api.vouchers()
      .then((result) => {
        if (cancelled) return;
        setVouchers(result);

        // TC-04: keep an offline pass for each spendable voucher while there is a
        // connection, so one is there when the signal is not.
        void refreshPasses(result);
      })
      .catch(() => { if (!cancelled) setVouchers([]); });

    return () => { cancelled = true; };
  }, []);

  if (vouchers === null) {
    return (
      <>
        <h1 className="section">My vouchers</h1>

        {/* The skeleton keeps the page's SHAPE while it loads, so the heading and
            the grid do not jump when the data lands. The announcement is separate
            and visually hidden: the grey bars mean nothing to a screen reader, and
            without role="status" this route is silence. */}
        <p className="visually-hidden" role="status">Loading your vouchers…</p>
        <div className="grid" aria-hidden="true">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton skeleton-card" />)}
        </div>
      </>
    );
  }

  return (
    <>
      <h1 className="section">My vouchers</h1>

      {/* TC-06: a bill the till sent to this phone that is not paid yet. */}
      {owed.map((p) => (
        <Link key={p.id} to={`/pay/${p.id}`} className="notice" style={{ display: 'block', marginBottom: 12 }}>
          Finish paying at {p.businessName}: {formatMoney(p.amountCents)} left on your bill. Tap to pay.
        </Link>
      ))}

      {vouchers.length === 0 ? (
        <div className="panel">
          <h2>Nothing here yet</h2>
          <p className="muted" style={{ margin: '8px 0 20px' }}>
            Vouchers you buy will appear here, ready to show at the counter.
          </p>
          <Link to="/" className="btn">Browse offers</Link>
        </div>
      ) : (
        <div className="grid">
          {vouchers.map((voucher) => {
            const spent = voucher.balanceCents < voucher.faceValueCents;

            return (
              <Link key={voucher.id} to={`/vouchers/${voucher.id}`} className="card">
                <div className="body">
                  <p className="faint">{voucher.businessName}</p>
                  <h3>{voucher.dealTitle}</h3>

                  <div className="price-row">
                    <span className="price">{formatMoney(voucher.balanceCents)}</span>
                    {spent && (
                      <span className="faint">of {formatMoney(voucher.faceValueCents)}</span>
                    )}
                  </div>

                  <p className="faint">
                    {voucher.status === 'active'
                      ? `Valid until ${new Date(voucher.validTo).toLocaleDateString()}`
                      : voucher.status === 'exhausted'
                        ? 'Fully used'
                        : voucher.status}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
